import React from 'react';
import { motion } from 'framer-motion';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';

// Icônes
import { BookHeart, Quote, PlayCircle } from 'lucide-react';

// --- Données du Livre d'Or, structurées ---
const testimonials = [
    {
        quote: "Je tiens à exprimer ma profonde gratitude envers ICE... Cette distinction prestigieuse reflète non seulement mes modestes efforts constants, mais aussi l’engagement et la loyauté du personnel du Conseil National de Sécurité... Cette reconnaissance nous encourage à poursuivre notre mission avec détermination et intégrité.",
        author: "Vice-Amiral Jean Dominique OKEMBA",
        title: "Ministre, Secrétaire Général du Conseil National de Sécurité, Congo",
        imageUrl: "http://ice-inter.com/wp-content/uploads/2024/10/251e0e2e-78f9-42f9-8f16-b0b319c0fff0-295x300.jpg"
    },
    {
        quote: "En ce Jour solennel où je reçois le Prix d’Excellence... je voudrais au nom du parlement Centrafricain et celui de la CEMAC témoigner au comité international d’excellence toute ma reconnaissance.",
        author: "Honorable Evariste NGAMANA",
        title: "Président du Parlement de la CEMAC",
        imageUrl: "http://ice-inter.com/wp-content/uploads/2023/10/EVARISTE-NGAMANA-1024x767.jpg"
    },
    {
        quote: "C’est pour moi un honneur et réel plaisir de faire partie des lauréats du prix d’excellence 2022/2023... Je le dédie à l’ensemble de la population centrafricaine qui soutient notre police dans sa montée en puissance.",
        author: "Général Bienvenu ZOKOUE",
        title: "Directeur Général de la Police Nationale, RCA",
        imageUrl: "http://ice-inter.com/wp-content/uploads/2023/10/ZOKOUE.jpg"
    },
    {
        quote: "Cette distinction remise en présence des plus hautes autorités... nous réconforte dans ce que nous sommes en train de faire et nous pousse à dépasser nos limites pour être à la hauteur d’attentes et objectifs plus ambitieux.",
        author: "Inspecteur Général Amadou KONATE",
        title: "Directeur Général des Douanes, Mali",
        imageUrl: "http://ice-inter.com/wp-content/uploads/2024/11/download-1.jpg"
    },
    {
        quote: "Au nom de la Fédération Capverdienne de Football et à mon propre nom, je remercie ICE pour la distinction et la confiance. Mes félicitations pour la grande mission que l’ICE apporte à l’Afrique et aux Africains.",
        author: "M. Mario SEMEDO",
        title: "Président de la Fédération de Football de Cap Vert",
        imageUrl: "http://ice-inter.com/wp-content/uploads/2024/11/marios-e.jpg"
    },
    {
        quote: "C’est avec beaucoup d’humilité et de surprise que je reçois ce Prix... Au-delà de ma modeste personne, c'est l’institution Gendarmerie Nationale qui est honorée. C’est un réconfort et un réel motif d’encouragement qui nous interpelle à persévérer.",
        author: "Général de brigade Gervais AKOUANGUE",
        title: "Commandant de la Gendarmerie Nationale, Congo",
        imageUrl: "http://ice-inter.com/wp-content/uploads/2025/04/31bdfdf8-2c4b-437b-a9a7-b039471f8802-300x169.jpg"
    }
];

const videos = [
    { src: "https://www.youtube.com/embed/oyJMsiyzFBU", title: "Le prix sous-régional de l'intégrité 2021 décerné au Dr Allah-Ridy Koné" },
    { src: "https://www.youtube.com/embed/DoQqBWXTZnI", title: "Des distinctions pour l'excellence Centrafricaine - ICE" },
    // Ajoutez d'autres vidéos YouTube ici si nécessaire
];

export const GuestbookPage: React.FC = () => {
    return (
        <PublicLayout>
            <SEOMetaTags 
                title="Livre d'Or - International Committee of Excellence (ICE)"
                description="Découvrez les témoignages et messages de reconnaissance des lauréats et personnalités distingués par l'ICE."
            />

            <div className="bg-[#0d1321] text-white">
                {/* --- Section Héros --- */}
                <section 
                    className="relative py-32 md:py-48 flex items-center justify-center text-center"
                    style={{
                        backgroundImage: 'url(http://ice-inter.com/wp-content/uploads/2024/11/mamou-zph-livre-dor-1024x682.jpg)',
                        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'
                    }}
                >
                    <div className="absolute inset-0 bg-[#0d1321]/80 backdrop-blur-sm"></div>
                    <div className="relative z-10 container mx-auto px-4">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                            className="text-4xl md:text-6xl font-bold text-white mb-4"
                        >
                            Paroles d'<span className="text-[#d2ab6a]">Excellence</span>
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto"
                        >
                            Les messages de gratitude et de reconnaissance des leaders et lauréats qui ont marqué l'histoire de notre organisation.
                        </motion.p>
                    </div>
                </section>

                {/* --- Grille des Témoignages --- */}
                <section className="py-20">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {testimonials.map((testimonial, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col h-full"
                                >
                                    <Quote className="w-12 h-12 text-[#d2ab6a]/50 mb-4" strokeWidth={1} />
                                    <blockquote className="text-white/80 italic leading-relaxed flex-grow">
                                        "{testimonial.quote}"
                                    </blockquote>
                                    <div className="mt-6 pt-6 border-t border-white/10 flex items-center">
                                        <img src={testimonial.imageUrl} alt={testimonial.author} className="w-16 h-16 rounded-full object-cover mr-4"/>
                                        <div>
                                            <p className="font-bold text-white text-lg">{testimonial.author}</p>
                                            <p className="text-[#d2ab6a] text-sm">{testimonial.title}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* --- Section Vidéos --- */}
                <section className="py-20 bg-white/5">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-white">
                                L'Excellence en <span className="text-[#d2ab6a]">Images</span>
                            </h2>
                            <p className="text-white/80 mt-4 max-w-2xl mx-auto">
                                Revivez les moments forts de nos cérémonies et les allocutions de nos lauréats.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            {videos.map((video, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: i * 0.15 }}
                                    className="bg-white/5 p-4 rounded-2xl border border-white/10"
                                >
                                    <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                                        <iframe
                                            src={video.src}
                                            title={video.title}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="w-full h-full"
                                        ></iframe>
                                    </div>
                                    <h3 className="text-white font-semibold mt-4 text-center">{video.title}</h3>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        </PublicLayout>
    );
};

export default GuestbookPage;